import { useState } from "react";
import { DownloadSimple, FileXls, FileText } from "@phosphor-icons/react";

/**
 * Single-button "Export" dropdown with Excel (.xlsx) and CSV (.csv) options.
 *
 * Props:
 *   endpoint       — backend path relative to REACT_APP_BACKEND_URL/api/ (e.g. "activities/export")
 *   filenameBase   — file stem without extension (e.g. "trip-activities-2026-06")
 *   params         — extra query params (object) sent on every download
 *   testidPrefix   — namespace for data-testids (default: "export")
 *   buttonLabel    — text shown on the button (default: "Export")
 */
export default function ExportButton({ endpoint, filenameBase, params = {}, testidPrefix = "export", buttonLabel = "Export" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setOpen(false);
    setBusy(true);
    try {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") search.set(k, String(v));
      });
      search.set("format", format);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/${endpoint}?${search.toString()}`;
      const token = localStorage.getItem("access_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${filenameBase}.${format}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 100);
    } catch (e) {
      alert("Export failed: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid={`${testidPrefix}-button`}
        className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm disabled:opacity-50"
      >
        <DownloadSimple size={16} weight="bold" />
        {busy ? "Exporting…" : buttonLabel}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-md shadow-lg z-30 overflow-hidden" data-testid={`${testidPrefix}-menu`}>
          <button
            type="button"
            onClick={() => download("xlsx")}
            data-testid={`${testidPrefix}-xlsx`}
            className="w-full text-left px-3 py-2.5 text-sm hover:bg-lime-50 hover:text-lime-700 flex items-center gap-2 border-b border-slate-100"
          >
            <FileXls size={16} weight="bold" className="text-emerald-600" />
            <span>Excel <span className="text-[10px] text-slate-400">.xlsx</span></span>
          </button>
          <button
            type="button"
            onClick={() => download("csv")}
            data-testid={`${testidPrefix}-csv`}
            className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2"
          >
            <FileText size={16} weight="bold" className="text-slate-500" />
            <span>CSV <span className="text-[10px] text-slate-400">.csv</span></span>
          </button>
        </div>
      )}
    </div>
  );
}
